import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import { memo, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { CRUISE_LINE_INFO, DEFAULT_CRUISE_LINE_FINE_PRINT_LINK } from 'constants/cruise'

interface Props {
  cruiseLine: App.CruiseLine
  bundleTermsAndConditions?: string | null
  compact?: boolean
}

function formatBundleTermsAndConditions(termsAndConditions: string): string {
  return termsAndConditions
    .replaceAll('\r\n', '\n')
    .replaceAll('\n', '  \n')
}

function CruiseFinePrint({ cruiseLine, bundleTermsAndConditions, compact = false }: Props) {
  const intl = useIntl()
  const { code: cruiseLineCode, name: cruiseLineName } = cruiseLine
  const formattedBundleTermsAndConditions = bundleTermsAndConditions?.trim()
    ? formatBundleTermsAndConditions(bundleTermsAndConditions.trim())
    : null

  const termsAndConditionsLink = useMemo(() => {
    if (cruiseLineCode && cruiseLineCode in CRUISE_LINE_INFO) {
      return CRUISE_LINE_INFO[cruiseLineCode as keyof typeof CRUISE_LINE_INFO].finePrintLink
    }

    return DEFAULT_CRUISE_LINE_FINE_PRINT_LINK
  }, [cruiseLineCode])

  const heading = (
    <Heading
      align={compact ? undefined : 'center'}
      variant={compact ? 'heading3' : 'heading2'}
    >
      <FormattedMessage
        id="5yHiib-00mPwk5MXY70i"
        defaultMessage="Booking Terms & Conditions"
        description="Heading for cruise booking terms and conditions section"
      />
    </Heading>
  )

  const content = (
    <Group direction="vertical" gap={16}>
      {!!formattedBundleTermsAndConditions &&
        <MarkdownRender
          content={formattedBundleTermsAndConditions}
          fontSize="medium"
          type="compact"
        />}
      <BodyText variant="medium">
        <FormattedMessage
          id="W5qE0pwnnODNMot_wg_a"
          defaultMessage="By completing your booking, you agree to the terms and conditions set forth by the cruise line operator. We acts solely as an intermediary and is not responsible for changes, cancellation policies, or specific rules established by the cruise company."
          description="Notice about agreeing to terms when booking a cruise"
        />
      </BodyText>
      <BodyText variant="medium">
        <FormattedMessage
          id="Pm_mH_9JTImodWtOulY-"
          defaultMessage="It is essential that you review and accept the official Terms and Conditions before finalizing your purchase. All policies regarding luggage, boarding, documentation, cancellations, and refunds are detailed in the operator’s official document."
          description="Notice about reviewing terms before purchase in the cancellation flow"
        />
      </BodyText>
      <TextLink size="medium" href={termsAndConditionsLink} target="_blank" weight="bold" underline>
        {intl.formatMessage(
          {
            id: 's4f6Kvi-cD_kRsA1mIuC',
            defaultMessage: 'Click here to view the Terms and Conditions for {cruiseLineName}',
            description: 'Link text to view cruise line terms and conditions',
          },
          { cruiseLineName },
        )}
      </TextLink>
    </Group>
  )

  if (compact) {
    return (
      <Group direction="vertical" gap={24}>
        {heading}
        {content}
      </Group>
    )
  }

  return (
    <Pane padding="32 32">
      <Group direction="vertical" gap={48}>
        {heading}
        <Pane>
          <LayoutContainer>
            {content}
          </LayoutContainer>
        </Pane>
      </Group>
    </Pane>
  )
}

export default memo(CruiseFinePrint)
